import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { User } from './model/entity-model';
import { SessionInformationService } from './session-information.service';

@Injectable()
export class AuthService {
    static config = {
        client_id: 'arkClient',
        accessName: 'access_token',
        refreshName: 'refresh_token',
    };

    private userSubject: BehaviorSubject<User>;
    public get currentUser(): User {
        return this.userSubject.value;
    }

    set currentUser(user: User) {
        this.userSubject.next(user);
    }

    private refreshTokenTimeout: any;

    constructor(private http: HttpClient, private router: Router, private sessionInfo: SessionInformationService) {
        this.userSubject = new BehaviorSubject<User>(null);
    }

    login(username: string, password: string) {
        return this.http.post<any>(`${environment.serviceName}/users/authenticate`, { username, password }, { withCredentials: true }).pipe(
            map((user) => {
                if (user) this.sessionInfo.setItem('refresh-token', user.refreshToken);
                this.setUser(user);
            })
        );
    }

    logout() {
        this.clearUser();
        this.router.navigate(['/login']);
    }

    private clearUser() {
        const token = this.sessionInfo.getItem('refresh-token');
        this.http.post<any>(`${environment.serviceName}/users/revoke-token`, { token }, { withCredentials: true }).subscribe();
        this.stopRefreshTokenTimer();
        this.userSubject.next(null);
    }

    private setUser(user: any) {
        this.userSubject.next(user);
        this.startRefreshTokenTimer();
        return user;
    }

    private startRefreshTokenTimer() {
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this.currentUser.jwtToken.split('.')[1]));

        // set a timeout to refresh the token every fifteen minutes
        const timeout = 15 * 60 * 1000;
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
    }

    refreshToken() {
        const token = this.sessionInfo.getItem('refresh-token');
        return this.http.post<any>(`${environment.serviceName}/users/refresh-token`, { token }, { withCredentials: true }).pipe(
            map((user) => {
                if (user) this.sessionInfo.setItem('refresh-token', user.refreshToken);
                return this.setUser(user);
            })
        );
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }

    hasRole(role: string) {
        return this.userHasRole(this.currentUser, role);
    }

    userHasRole(user, role: string) {
        if (!user || !user.roles) return false;

        return user.roles.some((roleName: string) => {
            // see if user perm starts with perm to check
            // return roleName.lastIndexOf(role, 0) === 0;
            return roleName === role;
        });
    }

    isAuthenticated() {
        return !!this.currentUser;
    }

    async getMemberUser(email: string, code: string) {
        const url = `${environment.serviceName}/portal/MemberUser`;
        const result = await firstValueFrom(this.http.post<{ user?: any; message?: string }>(url, { email: email, userCode: code }));
        if (result.user && !result.user.inactive) {
            this.currentUser = result.user;
                this.sessionInfo.setItem('refresh-token', result.user.refreshToken);
            } else this.clearUser();
        return result;
    }
}
